import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppTicks } from "./AppTicks";

// AppMsgActions - reply, edit and delete for everyone from a bubble (2026-10-14), and the ticks on the owner's own
// sent bubbles. The rules are habitat-free; the two browser classes take tiny interfaces so the suite drives them.
//
// ⛔ DECISION 9: a bubble's Reply / Edit / Delete only sets the composer's MODE (`AppComposer.SetMode`). A reply and an
// edit go when the owner presses Send; a delete only on the bar's "Delete" click (`AppComposer.ConfirmDelete`). The
// tick painter reads answers and writes attributes - nothing in this file can reach a sender.

export class AppMsgActionRules {
    public static readonly Kinds: string[] = ["reply", "edit", "revoke", "forward"];
    /** The kinds that put the composer in a MODE (forward opens a picker instead). */
    public static readonly ModeKinds: string[] = ["reply", "edit", "revoke"];
    /** The reaction row's action buttons carry this (`AppReactions.MakeRow`). */
    public static readonly ActAttr: string = "data-wac-act";
    public static readonly SnippetMax: number = 80;
    public static readonly Labels: Record<string, string> = { reply: "Reply", forward: "Forward", edit: "Edit", revoke: "Delete for everyone" };
    public static readonly PressDeleteWhy: string = "Press Delete to delete the message for everyone, or close the bar.";
    public static readonly BarId: string = "wac-modebar";
    /** Said under the box once `/send` took it; the live feed then shows the reply, the edit or the deletion. */
    public static readonly QueuedWords: string = "On its way - WhatsApp shows it in the chat in a moment.";

    /** Which actions a bubble offers: Reply always; Edit and Delete only on the owner's own (`.me`). Pure. */
    public static OfferedFor = (Own: boolean): string[] => {
        if (Own === true) { return ["reply", "forward", "edit", "revoke"]; }
        return ["reply", "forward"];
    };

    /** The bar's words for a mode. DOCUMENTED EXCEPTION: pure lookup. */
    public static BarWords = (Kind: string, Snippet: string): string => {
        if (Kind === "reply") { return "Replying to: " + Snippet; }
        if (Kind === "edit") { return "Editing your message - press Send to save the change."; }
        if (Kind === "revoke") { return "Delete this message for everyone? “" + Snippet + "”"; }
        return "";
    };

    /** A one-line snippet of a bubble's words (collapsed spaces, cut by code point). Pure. */
    public static SnippetOf = (Text: string): string => {
        const One: string = nz(Text).replace(/\s+/g, " ").trim();
        const Points: string[] = Array.from(One);
        if (Points.length <= AppMsgActionRules.SnippetMax) { return One; }
        return Points.slice(0, AppMsgActionRules.SnippetMax - 1).join("") + "…";
    };
}

/** The part of an element the bar and the painter use. */
export interface IMaNode {
    textContent: string | null;
    hidden: boolean;
    className: string;
    setAttribute(Name: string, Value: string): void;
    getAttribute(Name: string): string | null;
    removeAttribute(Name: string): void;
    appendChild(Child: IMaNode): IMaNode;
    addEventListener(Type: string, Fire: (Ev: Event) => void): void;
    querySelector(Sel: string): IMaNode | null;
    querySelectorAll(Sel: string): ArrayLike<IMaNode>;
}

export interface IMaDoc {
    createElement(Tag: string): IMaNode;
}

/** What the bar's buttons do - the composer. ⛔ `Delete` is `ConfirmDelete`, a click; `Close` sends nothing. */
export interface IMaBarHost {
    Close(): void;
    Delete(): string;
    Say(Text: string, Bad: boolean): void;
}

/**
 * The bar above the box (`#wac-modebar`): built ONCE, then only its words and `hidden` change (never rebuilt under
 * the pointer). One delegated click listener. A delete shows a "Delete" button; every mode has a close button.
 */
export class AppMsgActionBar {
    private Root: IMaNode;
    private Words: IMaNode;
    private DeleteButton: IMaNode;
    private Host: IMaBarHost;

    public constructor(Doc: IMaDoc, Host: IMaBarHost) {
        this.Host = Host;
        this.Root = Doc.createElement("div");
        this.Root.className = "wac-modebar";
        this.Root.setAttribute("id", AppMsgActionRules.BarId);
        this.Root.setAttribute("role", "status");
        this.Root.hidden = true;
        this.Words = Doc.createElement("span");
        this.Words.className = "wac-modewords";
        this.DeleteButton = Doc.createElement("button");
        this.DeleteButton.setAttribute("type", "button");
        this.DeleteButton.className = "btn danger sm";
        this.DeleteButton.setAttribute("data-wac-mode", "delete");
        this.DeleteButton.textContent = "Delete";
        const Close: IMaNode = Doc.createElement("button");
        Close.setAttribute("type", "button");
        Close.className = "btn ghost sm";
        Close.setAttribute("data-wac-mode", "close");
        Close.setAttribute("aria-label", "Cancel");
        Close.setAttribute("title", "Cancel");
        Close.textContent = "Cancel";
        this.Root.appendChild(this.Words);
        this.Root.appendChild(this.DeleteButton);
        this.Root.appendChild(Close);
        this.Root.addEventListener("click", this.OnClick);
    }

    public Element = (): IMaNode => { return this.Root; };

    public Show = (Kind: string, Snippet: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Words.textContent = AppMsgActionRules.BarWords(Kind, Snippet);
            this.DeleteButton.hidden = Kind !== "revoke";
            this.Root.setAttribute("data-kind", Kind);
            this.Root.hidden = false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMsgActionBar.Show");
        }
    };

    public Hide = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Root.hidden = true;
            this.Root.removeAttribute("data-kind");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMsgActionBar.Hide");
        }
    };

    private OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target: { closest?: (S: string) => IMaNode | null } = Ev.target as unknown as { closest?: (S: string) => IMaNode | null };
            if (Target === null || typeof Target.closest !== "function") { return; }
            const Button: IMaNode | null = (Target.closest as (S: string) => IMaNode | null)("[data-wac-mode]");
            if (Button === null) { return; }
            const What: string = nz(Button.getAttribute("data-wac-mode"));
            if (What === "close") { this.Host.Close(); return; }
            if (What === "delete") {
                const Why: string = this.Host.Delete();
                if (Why.length > 0) { this.Host.Say(Why, true); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMsgActionBar.OnClick");
        }
    };
}

/**
 * ⭐ TICKS IN THE FRAME: what each own bubble shows (`data-wac-tick`), the ask's `ticks` object, and an answer applied.
 * Written as ATTRIBUTES + a title; the drawing is CSS (`AppMsgTickPaint.Css`, icons as masks - no typed glyph).
 */
export class AppMsgTickPaint {
    /** The own bubbles the page can ask about: `.b.me` with a WhatsApp id, newest last on the page. */
    public static readonly OwnSel: string = ".b.me[data-mid]";

    /** The ask's `ticks`: each own bubble's shown status (0 = nothing yet), newest `AppTicks.MaxIds`. */
    public static AskOf = (Root: IMaNode | null): Record<string, number> => {
        const Out: Record<string, number> = {};
        if (getCoreUtils().KErrorMode === true || Root === null) { return Out; }
        try {
            const All: ArrayLike<IMaNode> = (Root as IMaNode).querySelectorAll(AppMsgTickPaint.OwnSel);
            const From: number = Math.max(0, All.length - AppTicks.MaxIds);
            for (let At: number = From; At < All.length; At = At + 1) {
                const Mid: string = nz(All[At].getAttribute("data-mid"));
                if (/^[A-Za-z0-9]{8,64}$/.test(Mid) === false) { continue; }
                Out[Mid] = AppTicks.StatusOf(Number(All[At].getAttribute(AppTicks.Attr)));
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMsgTickPaint.AskOf");
            return {};
        }
    };

    /** An answer's `ticks` applied: only BETTER statuses, only bubbles on the page. Returns how many changed. */
    public static Apply = (Root: IMaNode | null, Raw: unknown): number => {
        if (getCoreUtils().KErrorMode === true || Root === null) { return 0; }
        try {
            const Got: Record<string, number> | null = AppTicks.WireIn(Raw, false);
            if (Got === null) { return 0; }
            let Changed: number = 0;
            for (const Mid of Object.keys(Got as Record<string, number>)) {
                const Bubble: IMaNode | null = (Root as IMaNode).querySelector(".b.me[data-mid=\"" + Mid + "\"]");
                if (Bubble === null) { continue; }
                const Was: number = AppTicks.StatusOf(Number(Bubble.getAttribute(AppTicks.Attr)));
                const Now: number = (Got as Record<string, number>)[Mid];
                if (Now <= Was) { continue; }
                Bubble.setAttribute(AppTicks.Attr, String(Now));
                Bubble.setAttribute("data-wac-tickword", AppTicks.WordOf(Now));
                const Time: IMaNode | null = Bubble.querySelector(".t");
                if (Time !== null) {
                    Time.setAttribute("title", AppTicks.WordOf(Now));
                    if (Time.getAttribute("data-wac-time") === null) { Time.setAttribute("data-wac-time", nz(Time.textContent).trim()); }
                    Time.setAttribute("aria-label", nz(Time.getAttribute("data-wac-time")) + ", " + AppTicks.WordOf(Now));
                }
                Changed = Changed + 1;
            }
            return Changed;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMsgTickPaint.Apply");
            return 0;
        }
    };

    /**
     * A chat ask's body with `ticks` added - only when the page has own bubbles (a page with none sends no field, so
     * it is never answered early for ticks). "" in -> "" out.
     */
    public static WithTicks = (Body: string, Root: IMaNode | null): string => {
        if (getCoreUtils().KErrorMode === true || Body.length === 0) { return Body; }
        try {
            const Ticks: Record<string, number> = AppMsgTickPaint.AskOf(Root);
            if (Object.keys(Ticks).length === 0) { return Body; }
            const Doc: Record<string, unknown> = JSON.parse(Body) as Record<string, unknown>;
            Doc.ticks = Ticks;
            return JSON.stringify(Doc);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMsgTickPaint.WithTicks");
            return Body;
        }
    };

    /** An answer's raw text: its `ticks` applied, if it carries any. */
    public static FromAnswer = (Text: string, Root: IMaNode | null): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            const Doc: unknown = JSON.parse(nz(Text));
            if (Doc === null || typeof Doc !== "object" || (Doc as Record<string, unknown>).ticks === undefined) { return 0; }
            return AppMsgTickPaint.Apply(Root, (Doc as Record<string, unknown>).ticks);
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: the feed's own parser already decided this answer's fate; a body that is not
            // JSON simply carries no ticks.
            return 0;
        }
    };

    /**
     * The frame stylesheet: the tick after the bubble's time (`.t`), an SVG check drawn as a CSS MASK so its colour is
     * the theme's (`currentColor`; read = the accent) - no typed glyph. The word ("Sent" / "Delivered" / "Read") is the
     * time element's `title` and `aria-label` (`Apply`). ⚠ `data:` in a mask is an image fetch: the CSP's `img-src`
     * already allows `data:`.
     * DOCUMENTED EXCEPTION: a constant stylesheet. ERROR-SUPPRESSED-JUSTIFIED: returns a literal.
     */
    public static Css = (): string => {
        const One: string = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M5 12.5l4.5 4.5L19 7.5' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")";
        const Two: string = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M2 12.5l4.5 4.5L16 7.5M9.5 16.5l.5.5L22 7.5' fill='none' stroke='black' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")";
        return ".b.me[data-wac-tick]>.t::after{content:\"\";display:inline-block;width:16px;height:12px;margin-left:4px;"
            + "vertical-align:-1px;background:currentColor;-webkit-mask:" + One + " center/contain no-repeat;mask:" + One + " center/contain no-repeat}"
            + ".b.me[data-wac-tick=\"3\"]>.t::after,.b.me[data-wac-tick=\"4\"]>.t::after,.b.me[data-wac-tick=\"5\"]>.t::after{"
            + "-webkit-mask-image:" + Two + ";mask-image:" + Two + "}"
            + ".b.me[data-wac-tick=\"4\"]>.t::after,.b.me[data-wac-tick=\"5\"]>.t::after{background:var(--accent,#1f6feb)}";
    };
}
